import React from 'react';
import { LrButton } from 'components/LANDR/LrButton';
import { PlanFrequencyEnum } from '../../../../enum/frequency';
import { PrismicHTMLParser } from '../../../PrismicHTMLParser';
import { ApiPlan, ApiPlanFrequency, CommonPricingTranslations, Price } from '../types';
import { PrismicLink } from '../../../PrismicLink';
import { SliceContext } from '../../types';
import { CountryCurrency, findPriceForCountryCurrency } from '../../../../helpers';
import { PricingPlanProduct, PricingPlanProductDocument } from './PricingPlanProduct';
import { PricingPlanFeature, PricingPlanFeatureDocument } from './PricingPlanFeature';
import '../Pricing.scss';

type PricingCtaLink = {
    url: string;
};
export type PricingPlanDocument = {
    data: {
        api_plan: ApiPlan;
        name: string;
        pricing_cta: string;
        pricing_cta_link: PricingCtaLink;
        pricing_description: {
            html: string | null;
        };
        products: Array<{
            product: {
                document: Array<PricingPlanProductDocument | PricingPlanFeatureDocument>;
            };
        }>;
    };
};

export type PricingPlanProps = {
    plan: PricingPlanDocument;
    frequency: PlanFrequencyEnum;
    countryCurrency: CountryCurrency;
    commonTranslations: CommonPricingTranslations;
    sliceContext: SliceContext;
};

/**
 * Returns the plan frequency of the specified plan
 * @param {ApiPlan} apiPlan - The API plan to search
 * @param {Frequency} frequencyName - The name of the desired frequency (Yearly or Monthly)
 */
export const getPlanFrequency = (
    apiPlan: ApiPlan,
    frequencyName: PlanFrequencyEnum,
): ApiPlanFrequency => {
    const fallback = {} as ApiPlanFrequency;

    if (!apiPlan || !apiPlan.frequencies) {
        return fallback;
    }

    const currentFrequency = apiPlan.frequencies.find(f => f.interval === frequencyName);

    return currentFrequency || fallback;
};

/**
 * Returns the price of the specified plan
 * @param {ApiPlan} apiPlan - The API plan to search
 * @param {PlanFrequencyEnum} frequencyName - The name of the desired frequency (Yearly or Monthly)
 * @param {CountryCurrency} countryCurrency - The price's currency code
 */
export const getPlanPrice = (
    apiPlan: ApiPlan,
    frequencyName: PlanFrequencyEnum,
    countryCurrency: CountryCurrency,
): Price => {
    const fallback = {} as Price;
    const frequency = getPlanFrequency(apiPlan, frequencyName);

    if (!frequency || !frequency.prices) {
        return fallback;
    }

    const price = findPriceForCountryCurrency(frequency.prices, countryCurrency);
    return price || fallback;
};

const addCodeParamToCtaLink = (
    link: PricingCtaLink,
    code: string,
    planFrequency: string,
): PricingCtaLink => ({
    ...link,
    url: `${link.url}?plan=${code}&planFrequency=${planFrequency}`,
});

export class PricingPlan extends React.Component<PricingPlanProps> {
    render() {
        const { countryCurrency, frequency, commonTranslations, sliceContext } = this.props;
        const plan = this.props.plan.data;
        const apiPlanFrequency = getPlanFrequency(plan.api_plan, frequency);
        const showYearly = frequency === PlanFrequencyEnum.Yearly;

        const yearlyPrice = getPlanPrice(plan.api_plan, PlanFrequencyEnum.Yearly, countryCurrency);
        const monthlyPrice = getPlanPrice(
            plan.api_plan,
            PlanFrequencyEnum.Monthly,
            countryCurrency,
        );
        const monthlyAmount = showYearly
            ? Math.round(yearlyPrice.amount / 12)
            : monthlyPrice.amount;

        return (
            <div className="PricingPlan">
                <div className="PricingPlan-title">{plan.name}</div>

                <PrismicHTMLParser className="PricingPlan-description">
                    {plan.pricing_description.html}
                </PrismicHTMLParser>

                <div className="PricingPlan-price">
                    <span className="PricingPlan-price-currency">
                        {monthlyPrice.currencySymbol}
                    </span>
                    <span className="PricingPlan-price-amount">{monthlyAmount}</span>
                    <span className="PricingPlan-price-frequency">
                        {commonTranslations.per_month}
                    </span>
                </div>

                <div className="PricingPlan-discount">
                    {!showYearly && commonTranslations.yearly_upsell}
                    {showYearly && (
                        <>
                            {yearlyPrice.currencySymbol}
                            {yearlyPrice.amount}
                            &nbsp;
                            {commonTranslations.billed_yearly}
                        </>
                    )}
                    {yearlyPrice.yearlyDiscountPercent && (
                        <span className="u-text-discount u-ml-1">
                            {commonTranslations.save} {yearlyPrice.yearlyDiscountPercent * 100}%
                        </span>
                    )}
                </div>
                <div className="PricingPlan-cta">
                    <PrismicLink
                        link={addCodeParamToCtaLink(
                            plan.pricing_cta_link,
                            plan.api_plan.code,
                            frequency,
                        )}
                        slice={sliceContext}
                    >
                        <LrButton variant="primary" size="xsmall">
                            {plan.pricing_cta}
                        </LrButton>
                    </PrismicLink>
                </div>
                {plan.products.length > 0 && (
                    <ul className="PricingPlan-products">
                        {plan.products.map((item, index) => {
                            if (
                                item.product.document[0].__typename === 'PrismicPricingPlanProduct'
                            ) {
                                return (
                                    <PricingPlanProduct
                                        product={
                                            item.product.document[0] as PricingPlanProductDocument
                                        }
                                        apiPlanFrequency={apiPlanFrequency}
                                        countryCurrency={countryCurrency}
                                        key={index}
                                    />
                                );
                            } else if (
                                item.product.document[0].__typename === 'PrismicPricingPlanFeature'
                            ) {
                                const feature = item.product
                                    .document[0] as PricingPlanFeatureDocument;

                                return (
                                    <PricingPlanFeature
                                        feature={feature}
                                        key={index}
                                        index={index}
                                    />
                                );
                            } else {
                                return null;
                            }
                        })}
                    </ul>
                )}
            </div>
        );
    }
}
